/**
 * Transport pricing config + vehicle fleet data access (issue #23).
 *
 * Talks to the Go service via the shared api-client. Mirrors the JSON shapes
 * of go-service/internal/api/transport_config.go and vehicles.go:
 * GET/PUT /api/admin/transport-config, GET/POST/PATCH/DELETE
 * /api/admin/vehicles (DELETE soft-retires like suppliers).
 */
import { apiFetch } from "../../../shared/api-client";

export type BeyondZoneMode = "flat" | "per_km";

export interface TransportConfig {
  warehouse_address: string;
  warehouse_lat: number | null;
  warehouse_lng: number | null;
  base_zone_radius_km: number;
  base_zone_rate: number;
  beyond_zone_mode: BeyondZoneMode;
  extended_zone_rate: number;
  per_km_rate: number;
}

export interface TransportConfigResponse {
  config: TransportConfig;
  configured: boolean;
}

export interface TransportConfigSaveResponse {
  config: TransportConfig;
  /** False when the warehouse address could not be geocoded (or maps is unconfigured). */
  geocoded: boolean;
}

/** PUT payload — full config; coordinates are derived server-side. */
export type TransportConfigSave = Omit<
  TransportConfig,
  "warehouse_lat" | "warehouse_lng"
>;

export interface CompanyVehicle {
  id: string;
  name: string;
  vehicle_type: string;
  capacity_weight_kg: number | null;
  capacity_volume_m3: number | null;
  active: boolean;
  sort_order: number;
  created_at?: string | null;
}

export interface VehicleCreate {
  name: string;
  vehicle_type: string;
  capacity_weight_kg: number | null;
  capacity_volume_m3: number | null;
}

/** Update payload — id required; only supplied keys are written server-side. */
export interface VehicleUpdate extends Partial<VehicleCreate> {
  id: string;
  active?: boolean;
}

export async function getTransportConfig(
  signal?: AbortSignal,
): Promise<TransportConfigResponse> {
  return apiFetch<TransportConfigResponse>("/api/admin/transport-config", {
    signal,
  });
}

export async function saveTransportConfig(
  payload: TransportConfigSave,
): Promise<TransportConfigSaveResponse> {
  return apiFetch<TransportConfigSaveResponse>("/api/admin/transport-config", {
    method: "PUT",
    body: payload,
  });
}

export async function listVehicles(
  signal?: AbortSignal,
): Promise<CompanyVehicle[]> {
  const res = await apiFetch<{ vehicles: CompanyVehicle[] }>(
    "/api/admin/vehicles",
    { signal },
  );
  return res.vehicles ?? [];
}

export async function createVehicle(
  payload: VehicleCreate,
): Promise<CompanyVehicle> {
  const res = await apiFetch<{ vehicle: CompanyVehicle }>(
    "/api/admin/vehicles",
    { method: "POST", body: payload },
  );
  return res.vehicle;
}

export async function updateVehicle(
  payload: VehicleUpdate,
): Promise<CompanyVehicle> {
  const res = await apiFetch<{ vehicle: CompanyVehicle }>(
    "/api/admin/vehicles",
    { method: "PATCH", body: payload },
  );
  return res.vehicle;
}

/** Soft-retires the vehicle (active=false) — historical bookings keep it. */
export async function deleteVehicle(id: string): Promise<void> {
  await apiFetch<void>("/api/admin/vehicles", {
    method: "DELETE",
    query: { id },
  });
}

/* ─── Per-booking transport (workspace) ─────────────────────────────────── */

export interface BookingVehicle {
  vehicle_id: string;
  vehicle_name: string;
  vehicle_type: string;
  sort_order: number;
}

export interface BookingVehicleSave {
  vehicle_id: string;
}

export interface TransportPricingSnapshot {
  route_km: number;
  distance_source: string;
  /** Combined delivery + retrieval charge — the Transport line's rate. */
  total: number;
}

export interface TransportComputeResponse {
  route_km: number;
  distance_source: string;
  /** Combined delivery + retrieval charge — the Transport line's rate. */
  total: number;
  snapshot: TransportPricingSnapshot;
  /**
   * The FULL refreshed booking-items list. The caller MUST replace its
   * gear-list state with this (or refetch the workspace) — the gear PUT is
   * a delete+reinsert, so stale state wipes the new transport line.
   */
  gearItems: unknown[];
  /**
   * The post-bump bookings.items_revision (#242 C2). The gear editor advances
   * its echo token from this immediately — waiting for the workspace refetch
   * leaves a window where its own autosave 409s against the compute's bump.
   * Absent on older servers.
   */
  items_revision?: number | null;
}

export async function listBookingVehicles(
  bookingId: string,
  signal?: AbortSignal,
): Promise<{ vehicles: BookingVehicle[] }> {
  const res = await apiFetch<{ vehicles: BookingVehicle[] }>(
    `/api/admin/events/${bookingId}/vehicles`,
    { signal },
  );
  return { vehicles: res.vehicles ?? [] };
}

export async function putBookingVehicles(
  bookingId: string,
  vehicles: BookingVehicleSave[],
): Promise<{ vehicles: BookingVehicle[] }> {
  const res = await apiFetch<{ vehicles: BookingVehicle[] }>(
    `/api/admin/events/${bookingId}/vehicles`,
    { method: "PUT", body: { vehicles } },
  );
  return { vehicles: res.vehicles ?? [] };
}

export async function computeTransport(
  bookingId: string,
): Promise<TransportComputeResponse> {
  return apiFetch<TransportComputeResponse>(
    `/api/admin/events/${bookingId}/transport/compute`,
    { method: "POST", body: {} },
  );
}
